/* eslint-disable no-return-assign */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleMap, Marker, Circle, Autocomplete } from '@react-google-maps/api';
import Api from 'services/api';
import Loader from 'components/atoms/loader';
import { useGoogleMaps } from 'hooks/useGoogleMaps';
import { CitiesTypes } from 'store/modules/online_search_store/types';
import createTrack from 'services/actions/tracker_actions';
import { track } from 'services/tracking';
import { Range } from './styles';

const containerStyle = {
  width: '100%',
  height: '500px',
};

interface InnerBoundTypes {
  lat: Number | undefined;
  lng: Number | undefined;
}

interface BoundsTypes {
  ne: InnerBoundTypes;
  sw: InnerBoundTypes;
}

interface MapWithControlsProps {
  setModalLocationOpened: React.Dispatch<React.SetStateAction<boolean>>;
  setAddressSelected: React.Dispatch<React.SetStateAction<string>>;
  addressSelected: string;
  bounds: BoundsTypes;
  setBounds: React.Dispatch<React.SetStateAction<BoundsTypes>>;
  position: google.maps.LatLngLiteral | null;
  setPosition: React.Dispatch<React.SetStateAction<google.maps.LatLngLiteral | null>>;
  radius: number;
  setRadius: React.Dispatch<React.SetStateAction<number>>;
  setCitiesGeolocation: React.Dispatch<React.SetStateAction<Array<CitiesTypes>>>;
}

interface CityTypes {
  id: Number;
  name: string;
}

const MapWithControls: React.FC<MapWithControlsProps> = (props: MapWithControlsProps) => {
  const {
    setModalLocationOpened,
    setAddressSelected,
    addressSelected,
    bounds,
    setBounds,
    position,
    setPosition,
    radius,
    setRadius,
    setCitiesGeolocation,
  } = props;

  const { isLoaded } = useGoogleMaps();
  const [useMyAddress, setUseMyAddress] = useState<boolean>(false);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const circleRef = useRef<google.maps.Circle | null>(null);

  const rangeRef = useRef<HTMLInputElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  useEffect(() => {
    if (!useMyAddress && addressSelected !== '') return;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setPosition(coords);
          setAddressSelected('');
        },
        (error) => {
          console.error('Erro ao obter localização:', error);
        },
      );
    } else {
      console.error('Geolocalização não é suportada neste navegador.');
    }
  }, [useMyAddress]);

  useEffect(() => {
    if (circleRef.current) {
      const tempBounds = circleRef.current.getBounds();

      if (tempBounds) {
        const ne = tempBounds?.getNorthEast();
        const sw = tempBounds?.getSouthWest();
        setBounds({
          ne: { lat: ne?.lat(), lng: ne?.lng() },
          sw: { lat: sw?.lat(), lng: sw?.lng() },
        });
      }
    }
  }, [position, radius]);

  const searchCities = () => {
    if (!bounds) return;

    createTrack('Radar - Confirmou busca de endereço no raio de localização');

    const { ne, sw } = bounds;

    Api.get(
      `/cities?sw_lat=${sw.lat}&sw_lng=${sw.lng}&ne_lat=${ne.lat}&ne_lng=${ne.lng}}`,
      {},
    ).then((response) => {
      if (response?.data) {
        const citiesResponse = response.data?.data ? response.data?.data : response.data;
        const formattedCities = citiesResponse.map((city: CityTypes) => ({
          label: city.name,
          value: city.id,
        }));

        setCitiesGeolocation(formattedCities);
      }
    });
  };

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    setAddressSelected(place?.formatted_address || '');
    createTrack('Radar - Buscou endereço por raio de atuação');
    track('radar.buscou_endereco_raio_atuacao');

    if (place?.geometry?.location) {
      const { location } = place.geometry;

      setPosition({
        lat: location.lat(),
        lng: location.lng(),
      });
    }
  };

  const addressPermission = () => {
    setUseMyAddress(true);
  };

  const updateLabelPosition = () => {
    const range = rangeRef.current;
    const label = labelRef.current;

    if (range && label) {
      const min = parseFloat(range.min);
      const max = parseFloat(range.max);
      const percent = (radius - min) / (max - min);

      const rangeWidth = range.offsetWidth;
      const thumbWidth = 20;

      const offset = percent * (rangeWidth - thumbWidth) + thumbWidth / 2;
      label.style.left = `${offset}px`;
    }
  };

  useEffect(() => {
    updateLabelPosition();
  }, [radius]);

  if (!isLoaded) {
    return (
      <div className="modal-body py-5">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <div className="modal-body">
        <div className="w-full max-w-xl text-center">
          <Autocomplete
            onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)}
            onPlaceChanged={handlePlaceChanged}
          >
            <input
              id="adress-input"
              type="text"
              placeholder="Digite um endereço..."
              className="form-control"
              value={addressSelected}
              onChange={(e) => setAddressSelected(e.target.value)}
            />
          </Autocomplete>
        </div>
        {false && position && (
          <div className="row">
            <div className="col-md-12">
              <button
                type="button"
                className="btn btn-link px-0 py-1"
                onClick={() => {
                  addressPermission();
                }}
              >
                Utilizar minha localização
              </button>
            </div>
          </div>
        )}
        {position && (
          <Range className="w-full max-w-xl text-center mt-4">
            <div className="form-group d-flex">
              <span className="mr-2">10km</span>
              <div className="position-relative w-100 d-flex">
                <span ref={labelRef} className="position-absolute range-number font-weight-bold">
                  {radius / 1000}km
                </span>
                <input
                  ref={rangeRef}
                  type="range"
                  min={10000}
                  max={1000000}
                  step={10000}
                  value={radius}
                  onChange={(e) => setRadius(Number(e.target.value))}
                  className="form-control-range"
                  id="formControlRange"
                />
              </div>
              <span className="ml-2">1000km</span>
            </div>
          </Range>
        )}
        {position && (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={position}
            zoom={15}
            onLoad={onMapLoad}
          >
            <Marker position={position} />
            <Circle
              center={position}
              radius={radius}
              onLoad={(circle) => {
                circleRef.current = circle;
              }}
              options={{
                fillColor: '#007BFF',
                fillOpacity: 0.3,
                strokeColor: '#007BFF',
                strokeOpacity: 0.8,
                strokeWeight: 2,
              }}
            />
          </GoogleMap>
        )}
      </div>
      {position && (
        <div className="modal-footer justify-content-center">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              searchCities();
              setModalLocationOpened(false);
            }}
          >
            Confirmar meu endereço
          </button>
        </div>
      )}
    </>
  );
};

export default MapWithControls;
